import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Loader,
  Modal,
  MultiSelect,
  Paper,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  Title,
  TextInput,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { DatePickerInput, DateTimePicker } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
  IconCalendarEvent,
  IconCalendarPlus,
  IconBellRinging,
  IconCheck,
  IconCircle,
  IconClock,
  IconCopy,
  IconEdit,
  IconRefresh,
  IconSparkles,
  IconTrash,
  IconUsers,
  IconX,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DataSheetGrid, checkboxColumn, keyColumn, textColumn } from 'react-datasheet-grid';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../lib/auth/useAuth';
import { PageHeader } from '../components/PageHeader';
import { EmptyState } from '../components/EmptyState';

dayjs.extend(customParseFormat);

type Role = { id: string; name: string };
type Ressort = { id: string; name: string };

type EventRow = {
  id: string;
  title: string;
  event_type: string;
  start_time: string;
  end_time: string | null;
  description: string | null;
  location_name: string | null;
  requires_attendance_check: boolean;
};

type EventForm = {
  title: string;
  event_type: string | null;
  description: string;
  start_time: Date | null;
  end_time: Date | null;
  location_name: string;
  requires_attendance_check: boolean;
  role_ids: string[];
  ressort_ids: string[];
};

type EventChangeType = 'created' | 'updated';

type EventChangeNotifyResult = {
  queued?: boolean;
  skipped?: boolean;
  batch_id?: string;
  event_ids?: string[];
  event_count?: number;
  target?: string;
  change_reason?: string | null;
  note?: string;
  flush_after?: string | null;
  recipients?: number;
  users_with_tokens?: number;
  tokens_total?: number;
  sent_users?: number;
  sent_tokens?: number;
  throttle_bypassed?: boolean;
};

type EventChangeNotificationOptions = {
  changed_fields?: string[];
  urgent_location_only?: boolean;
  previous_events?: Array<{
    id: string;
    location_name?: string | null;
    location?: string | null;
  }>;
};

const predefinedLocations = [
  'Buergerhaus Daaden',
  'Alpenrod',
  'Hachenburg',
  'Weitefeld',
];

function parseJsonList(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((x) => String(x));
  if (typeof value === 'string') {
    try {
      const j = JSON.parse(value);
      if (Array.isArray(j)) return j.map((x) => String(x));
    } catch {
      return [];
    }
  }
  return [];
}

function parseDateInput(s: string): Date | null {
  const t = (s ?? '').trim();
  if (!t) return null;

  // Preferred formats for admin bulk import (Excel copy/paste friendly).
  const formats = [
    // ISO-ish
    'YYYY-MM-DD',
    'YYYY-MM-DD HH:mm',
    'YYYY-MM-DD HH:mm:ss',
    'YYYY-MM-DDTHH:mm',
    'YYYY-MM-DDTHH:mm:ss',
    'YYYY/MM/DD',
    'YYYY/MM/DD HH:mm',
    'YYYY.MM.DD',
    'YYYY.MM.DD HH:mm',
    // German / EU
    'DD.MM.YYYY',
    'DD.MM.YYYY HH:mm',
    'DD.MM.YYYY H:mm',
    'DD.MM.YYYY HH:mm:ss',
    'DD.MM.YY HH:mm',
    'DD.MM.YY',
    'D.M.YYYY',
    'D.M.YYYY HH:mm',
    'D.M.YYYY H:mm',
    'D.M.YY',
    'DD/MM/YYYY',
    'DD/MM/YYYY HH:mm',
    'D/M/YYYY',
    'D/M/YYYY HH:mm',
    'DD-MM-YYYY',
    'DD-MM-YYYY HH:mm',
    'D-M-YYYY',
    'D-M-YYYY HH:mm',
  ];

  const strict = dayjs(t, formats, true);
  if (strict.isValid()) return strict.toDate();

  // Last resort: let the runtime try.
  const fallback = dayjs(t);
  return fallback.isValid() ? fallback.toDate() : null;
}

function normalizeName(s: string) {
  return s.trim().toLowerCase();
}

function defaultRange() {
  const now = new Date();
  // From: today (start of day)
  const from = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  // To: end of current month (23:59:59.999)
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
  return { from, to };
}

function rangeForDate(date: Date) {
  const from = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
  const to = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
  return { from, to };
}

function eventPushResultMessage(result: EventChangeNotifyResult | null | undefined, fallbackCount = 1) {
  if (!result) return 'Push wurde nicht ausgeführt.';
  if (result.skipped) return result.note ?? 'Push wurde übersprungen.';
  if (result.queued) {
    return `${Number(result.event_count ?? fallbackCount)} Termine geplant. Push wird in wenigen Minuten gebündelt versendet.`;
  }
  return `Empfaenger: ${Number(result.recipients ?? 0)} · erreicht: ${Number(result.sent_users ?? 0)}`;
}

function reminderDateLabel(event: EventRow) {
  const start = new Date(event.start_time);
  const end = event.end_time ? new Date(event.end_time) : null;
  const dateStr = start.toLocaleDateString('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const startTime = start.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  const endTime = end ? end.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '';
  return endTime ? `${dateStr} ${startTime} - ${endTime}` : `${dateStr} ${startTime}`;
}

function defaultReminderTitle(event: EventRow) {
  const start = new Date(event.start_time);
  const dateStr = start.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  return `Bitte Rückmeldung: ${dateStr}`;
}

function defaultReminderBody(event: EventRow) {
  return `Bitte gib im Kalender kurz deine Rückmeldung zum Termin ${reminderDateLabel(event)}: Zusagen oder Absagen.`;
}

function emptyForm(): EventForm {
  const now = new Date();
  const end = new Date(now.getTime() + 60 * 60 * 1000);
  return {
    title: '',
    event_type: null,
    description: '',
    start_time: now,
    end_time: end,
    location_name: '',
    requires_attendance_check: false,
    role_ids: [],
    ressort_ids: [],
  };
}

type BulkRow = {
  title: string | null;
  event_type: string | null;
  start_time: string | null;
  end_time: string | null;
  location_name: string | null;
  description: string | null;
  roles: string | null;
  ressorts: string | null;
  attendance: boolean;
  result: string | null;
};

type DayBlock = {
  id: string;
  title: string;
  event_type: string;
  location_name: string | null;
  start: Date;
  end: Date;
  startMin: number;
  endMin: number;
  col: number;
  colCount: number;
  overlaps: boolean;
};

type AttendanceRow = {
  user_id: string;
  status: string;
  late_arrival_time: string | null;
  attendance_created_at: string | null;
  first_name: string | null;
  last_name: string | null;
  nickname: string | null;
  avatar_url: string | null;
};

type AttendanceStatusRow = {
  event_id: string;
  status: string;
  late_arrival_time?: string | null;
};

type AttendanceSummary = {
  yes: number;
  no: number;
  abs: number;
  unk: number;
  late: number;
  total: number;
};

function dateStringToDateLocal(s: unknown): Date | null {
  if (!s) return null;
  if (s instanceof Date) return s;
  if (typeof s !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const dt = new Date(y, mo - 1, d);
  return isNaN(dt.getTime()) ? null : dt;
}

function dateTimeStringToDateLocal(s: unknown): Date | null {
  if (!s) return null;
  if (s instanceof Date) return s;
  if (typeof s !== 'string') return null;

  // Mantine dates v8 emits strings like `YYYY-MM-DD` or `YYYY-MM-DD HH:mm:ss`.
  const t = s.trim();
  if (!t) return null;
  const isoLike = t.includes('T') ? t : t.replace(' ', 'T');
  const dt = new Date(isoLike);
  return isNaN(dt.getTime()) ? null : dt;
}

function eventTypeAccent(type: string): string {
  const t = (type ?? '').toLowerCase();
  if (t.includes('tanz')) return 'rgba(120, 255, 160, 0.75)';
  if (t.includes('schauspiel')) return 'rgba(120, 180, 255, 0.75)';
  if (t.includes('chor')) return 'rgba(255, 160, 255, 0.70)';
  if (t.includes('gesamt') || t.includes('musikal')) return 'rgba(255, 210, 120, 0.75)';
  if (t.includes('deadline')) return 'rgba(255, 140, 80, 0.75)';
  return 'var(--app-border-2)';
}

function nameLabel(row: { nickname?: string | null; first_name?: string | null; last_name?: string | null }) {
  const nn = (row.nickname ?? '').trim();
  const fn = (row.first_name ?? '').trim();
  const ln = (row.last_name ?? '').trim();
  if (nn) return nn;
  const base = [fn, ln].filter(Boolean).join(' ');
  return base || 'Unbekannt';
}

function normalizeStatus(raw: string): 'yes' | 'no' | 'absence' | 'unknown' {
  const s = (raw ?? '').toLowerCase().trim();
  if (['present', 'accepted', 'yes', 'going', 'zugesagt'].includes(s)) return 'yes';
  if (['absent', 'declined', 'no', 'not_going', 'abgesagt'].includes(s)) return 'no';
  if (s === 'user_absence') return 'absence';
  return 'unknown';
}

function formatLateArrivalTime(raw: string | null | undefined): string {
  const match = /^(\d{1,2}):(\d{2})/.exec((raw ?? '').trim());
  if (!match) return '';
  return `${match[1].padStart(2, '0')}:${match[2]}`;
}

function normalizeKey(s: string) {
  return (s ?? '').toString().trim().toLowerCase();
}

function parseDateParts(cell: string): { y: number; m: number; d: number } | null {
  const t = (cell ?? '').trim();
  if (!t) return null;

  let m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
  if (m) return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };

  m = /^(\d{1,2})\.(\d{1,2})\.(\d{2,4})$/.exec(t);
  if (m) {
    const d = Number(m[1]);
    const mo = Number(m[2]);
    let y = Number(m[3]);
    if (y >= 0 && y < 100) y = 2000 + y;
    return { y, m: mo, d };
  }

  return null;
}

function parseTimeRange(cell: string): { sh: number; sm: number; eh: number; em: number } | null {
  const t = (cell ?? '').trim();
  if (!t) return null;

  const re =
    /(\d{1,2})(?:[:.]?(\d{2}))?\s*(?:uhr)?\s*[-–]\s*(\d{1,2})(?:[:.]?(\d{2}))?\s*(?:uhr)?/i;
  const m = re.exec(t);
  if (!m) return null;

  const sh = Number(m[1]);
  const sm = m[2] != null ? Number(m[2]) : 0;
  const eh = Number(m[3]);
  const em = m[4] != null ? Number(m[4]) : 0;

  if (![sh, sm, eh, em].every((x) => Number.isFinite(x))) return null;
  if (sh < 0 || sh > 23 || eh < 0 || eh > 23) return null;
  if (sm < 0 || sm > 59 || em < 0 || em > 59) return null;

  return { sh, sm, eh, em };
}

function looksLikeHeader(cell: string) {
  const t = (cell ?? '').trim();
  return t.endsWith(':') && t.length >= 4;
}

function looksLikeEventTitle(cell: string) {
  const t = normalizeKey(cell);
  return (
    t.includes('probe') ||
    t.includes('termin') ||
    t.includes('auftritt') ||
    t.includes('deadline') ||
    t.includes('versammlung') ||
    t.includes('jhv') ||
    t.includes('mitgliederversammlung')
  );
}

function looksLikeDetails(cell: string) {
  const t = normalizeKey(cell);
  return t.includes('szene') || t.includes('akt') || t.includes('finale') || t.includes('s.');
}

function inferEventTypeFromText(text: string, eventTypes: string[]): string | null {
  const list = (eventTypes ?? []).filter((x) => String(x ?? '').trim().length > 0);
  if (!list.length) return null;

  const t = normalizeKey(text);

  const bySubstr = (substr: string) =>
    list.find((x) => normalizeKey(x).includes(substr)) ?? null;

  if (t.includes('tanz')) return bySubstr('tanz') ?? list[0];
  if (t.includes('schauspiel')) return bySubstr('schauspiel') ?? list[0];
  if (t.includes('chor')) return bySubstr('chor') ?? list[0];
  if (t.includes('gesamt')) return bySubstr('gesamt') ?? list[0];
  if (t.includes('musikal')) return bySubstr('gesamt') ?? bySubstr('musikal') ?? list[0];
  if (t.includes('deadline')) return bySubstr('deadline') ?? list[0];

  return list[0];
}

function emptyBulkRow(overrides?: Partial<BulkRow>): BulkRow {
  return {
    title: '',
    event_type: '',
    start_time: '',
    end_time: '',
    location_name: '',
    description: '',
    roles: '',
    ressorts: '',
    attendance: false,
    result: '',
    ...(overrides ?? {}),
  };
}

function padBulkRows(rows: BulkRow[], minRows = 12) {
  const out = [...rows];
  const target = Math.max(minRows, rows.length + 6); // a few extra blank rows for edits
  while (out.length < target) out.push(emptyBulkRow());
  return out;
}

function splitCommaList(s: string | null | undefined): string[] {
  return (s ?? '')
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
}

function joinCommaList(values: string[]): string {
  return (values ?? [])
    .map((x) => (x ?? '').trim())
    .filter(Boolean)
    .join(', ');
}

function previewStatusForRow(
  row: BulkRow,
  ctx: { eventTypes: string[]; roleNames: string[]; ressortNames: string[] },
): { level: 'ok' | 'warn' | 'error'; label: string; message?: string } {
  const title = (row.title ?? '').trim();
  if (!title) return { level: 'warn', label: 'Leer', message: 'Titel fehlt (wird beim Import ignoriert).' };

  const result = (row.result ?? '').trim();
  if (result === 'OK') return { level: 'ok', label: 'OK' };

  const eventTypes = new Set((ctx.eventTypes ?? []).map(normalizeName));
  const type = normalizeName(row.event_type ?? '');
  if (!type || !eventTypes.has(type)) return { level: 'error', label: 'Fehler', message: 'Unbekannter Typ' };

  const start = parseDateInput(row.start_time ?? '');
  if (!start) return { level: 'error', label: 'Fehler', message: 'Start ungültig' };

  const endRaw = (row.end_time ?? '').trim();
  const end = endRaw ? parseDateInput(endRaw) : new Date(start.getTime() + 60 * 60 * 1000);
  if (endRaw && !end) return { level: 'error', label: 'Fehler', message: 'Ende ungültig' };
  if (end && end.getTime() < start.getTime()) return { level: 'error', label: 'Fehler', message: 'Ende < Start' };

  const rolesByName = new Set((ctx.roleNames ?? []).map(normalizeName));
  for (const name of splitCommaList(row.roles)) {
    if (!rolesByName.has(normalizeName(name))) return { level: 'error', label: 'Fehler', message: `Rolle fehlt: ${name}` };
  }

  const ressortsByName = new Set((ctx.ressortNames ?? []).map(normalizeName));
  for (const name of splitCommaList(row.ressorts)) {
    if (!ressortsByName.has(normalizeName(name))) return { level: 'error', label: 'Fehler', message: `Ressort fehlt: ${name}` };
  }

  if (result.toLowerCase().startsWith('unbekannt:')) return { level: 'warn', label: 'Hinweis', message: result };
  if (result) return { level: 'error', label: 'Fehler', message: result };

  return { level: 'ok', label: 'OK' };
}

function parseProbenplanPaste(
  raw: string,
  opts: {
    defaultLocation: string | null;
    defaultAttendance: boolean;
    eventTypes: string[];
    knownRoles: string[];
    knownRessorts: string[];
  },
): {
  rows: BulkRow[];
  warnings: string[];
  detected: { dateCol: number; timeCol: number; lines: number; events: number; columns: number };
} {
  const warnings: string[] = [];
  const text = (raw ?? '').replace(/\r/g, '').trim();
  if (!text) {
    return {
      rows: [],
      warnings: ['Kein Text. Bitte aus Excel kopieren und hier einfuegen.'],
      detected: { dateCol: 0, timeCol: 1, lines: 0, events: 0, columns: 0 },
    };
  }

  const lines = text.split('\n').map((l) => l.replace(/\s+$/g, ''));
  const table = lines.map((l) => l.split('\t').map((c) => (c ?? '').trim()));
  const maxCols = Math.max(1, ...table.map((r) => r.length));

  // Detect columns by stats.
  const dateHits = Array.from({ length: maxCols }).map(() => 0);
  const timeHits = Array.from({ length: maxCols }).map(() => 0);
  for (const row of table) {
    for (let i = 0; i < maxCols; i++) {
      const cell = (row[i] ?? '').trim();
      if (!cell) continue;
      if (parseDateParts(cell)) dateHits[i]++;
      if (parseTimeRange(cell)) timeHits[i]++;
    }
  }

  const argmax = (arr: number[], skipIdx?: number) => {
    let best = 0;
    let bestV = -1;
    for (let i = 0; i < arr.length; i++) {
      if (skipIdx != null && i === skipIdx) continue;
      if (arr[i] > bestV) {
        bestV = arr[i];
        best = i;
      }
    }
    return best;
  };

  const dateCol = argmax(dateHits);
  const timeCol = argmax(timeHits, dateCol);

  const roleByKey = new Map<string, string>();
  for (const r of opts.knownRoles ?? []) {
    const k = normalizeKey(r);
    if (k) roleByKey.set(k, r);
  }
  const ressortByKey = new Map<string, string>();
  for (const r of opts.knownRessorts ?? []) {
    const k = normalizeKey(r);
    if (k) ressortByKey.set(k, r);
  }

  let currentDate: { y: number; m: number; d: number } | null = null;
  let sectionTitle = '';
  let sectionType: string | null = null;

  const rows: BulkRow[] = [];

  for (let idx = 0; idx < table.length; idx++) {
    const row = table[idx];

    // Date (carry forward)
    const dateCell = (row[dateCol] ?? '').trim();
    const dp = parseDateParts(dateCell);
    if (dp) currentDate = dp;

    // Time (prefer detected timeCol, fallback any cell)
    let timeCell = (row[timeCol] ?? '').trim();
    let tr = parseTimeRange(timeCell);
    if (!tr) {
      for (const c of row) {
        const cand = (c ?? '').trim();
        const t2 = parseTimeRange(cand);
        if (t2) {
          timeCell = cand;
          tr = t2;
          break;
        }
      }
    }

    const rest = row
      .filter((_, i) => i !== dateCol && i !== timeCol)
      .map((c) => (c ?? '').trim());
    const nonEmpty = rest.filter((c) => c.length > 0);

    // Header line? (scan full row because Excel layouts vary; header may sit in the time column)
    if (!tr) {
      const all = row.map((c) => (c ?? '').trim()).filter((c) => c.length > 0);
      const header = all.find((c) => looksLikeHeader(c));
      if (header) {
        sectionTitle = header.replace(/:\s*$/, '').trim();
        sectionType = inferEventTypeFromText(sectionTitle, opts.eventTypes);
      }
      continue;
    }

    if (!currentDate) {
      warnings.push(`Zeile ${idx + 1}: Datum fehlt (Zeit="${timeCell}")`);
      continue;
    }

    const start = new Date(currentDate.y, currentDate.m - 1, currentDate.d, tr.sh, tr.sm, 0, 0);
    let end = new Date(currentDate.y, currentDate.m - 1, currentDate.d, tr.eh, tr.em, 0, 0);
    if (end <= start) end = new Date(end.getTime() + 24 * 60 * 60 * 1000);

    let title = '';
    let details = '';
    let groupText = '';
    let notes = '';

    if (nonEmpty.length) {
      const first = nonEmpty[0];
      if (sectionTitle && !looksLikeEventTitle(first) && looksLikeDetails(first)) {
        title = sectionTitle;
        details = first;
        groupText = nonEmpty[1] ?? '';
        notes = nonEmpty.slice(2).join(' ');
      } else {
        title = first || sectionTitle || 'Termin';
        details = nonEmpty[1] ?? '';
        groupText = nonEmpty[2] ?? '';
        notes = nonEmpty.slice(3).join(' ');
      }
    } else {
      title = sectionTitle || 'Termin';
    }

    // Clean up common Excel header formatting like trailing ":".
    title = title.replace(/:\s*$/, '').trim();

    const eventType = (sectionType ?? inferEventTypeFromText(title, opts.eventTypes) ?? '').trim() || (opts.eventTypes?.[0] ?? '');

    // Roles/Ressorts: try to distribute by known names. Unknown parts go to description.
    const rolesOut: string[] = [];
    const ressortsOut: string[] = [];
    const unknown: string[] = [];

    const groupT = groupText.trim();
    if (groupT) {
      const tokens = groupT.split(',').map((x) => x.trim()).filter(Boolean);
      for (const tok of tokens) {
        const k = normalizeKey(tok);
        const role = roleByKey.get(k);
        const res = ressortByKey.get(k);
        if (role) rolesOut.push(role);
        else if (res) ressortsOut.push(res);
        else if (/^alle\b/.test(k)) {
          const alle = ressortByKey.get('alle') ?? 'Alle';
          if (!ressortsOut.includes(alle)) ressortsOut.push(alle);
          if (k !== 'alle') unknown.push(tok);
        } else {
          unknown.push(tok);
        }
      }
    }

    const descParts: string[] = [];
    if (details && normalizeKey(details) !== normalizeKey(title)) descParts.push(details);
    if (notes) descParts.push(notes);
    if (unknown.length) descParts.push(`Hinweis: ${unknown.join(', ')}`);
    const description = descParts.join('\n').trim();

    rows.push(
      emptyBulkRow({
        title,
        event_type: eventType,
        start_time: dayjs(start).format('YYYY-MM-DD HH:mm'),
        end_time: dayjs(end).format('YYYY-MM-DD HH:mm'),
        location_name: opts.defaultLocation ?? '',
        description,
        roles: rolesOut.join(', '),
        ressorts: ressortsOut.join(', '),
        attendance: opts.defaultAttendance,
        result: unknown.length ? `Unbekannt: ${unknown.join(', ')}` : '',
      }),
    );
  }

  if (!rows.length) warnings.push('Keine Termine erkannt. Tipp: Es muessen Zeitspannen wie "10 Uhr - 13 Uhr" im Paste enthalten sein.');

  return {
    rows,
    warnings,
    detected: { dateCol, timeCol, lines: lines.length, events: rows.length, columns: maxCols },
  };
}

export function CalendarPage() {
  const qc = useQueryClient();
  const { session, capabilities } = useAuth();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    document.title = 'Kalender – Musical!Kultur Admin';
  }, []);
  const [{ from, to }, setRange] = useState(defaultRange());
  const [activeTab, setActiveTab] = useState<string | null>('list');
  const [day, setDay] = useState<Date>(new Date());
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit' | 'duplicate'>('create');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<EventForm>(emptyForm());
  const [notifyEventChange, setNotifyEventChange] = useState(false);
  const [bulkNotifyEventChange, setBulkNotifyEventChange] = useState(false);
  const [highlightedEventIds, setHighlightedEventIds] = useState<Set<string>>(() => new Set());

  const [attOpen, setAttOpen] = useState(false);
  const [attEvent, setAttEvent] = useState<EventRow | null>(null);
  const [attSearch, setAttSearch] = useState('');
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderTitle, setReminderTitle] = useState('');
  const [reminderBody, setReminderBody] = useState('');

  const [bulkRows, setBulkRows] = useState<BulkRow[]>(
    Array.from({ length: 12 }).map(() => ({
      title: '',
      event_type: '',
      start_time: '',
      end_time: '',
      location_name: '',
      description: '',
      roles: '',
      ressorts: '',
      attendance: false,
      result: '',
    })),
  );

  const [pasteRaw, setPasteRaw] = useState('');
  const [pasteWarnings, setPasteWarnings] = useState<string[]>([]);
  const [pasteDetected, setPasteDetected] = useState<{ dateCol: number; timeCol: number; lines: number; events: number } | null>(null);
  const [pasteDefaultLocation, setPasteDefaultLocation] = useState<string | null>(null);
  const [pasteDefaultAttendance, setPasteDefaultAttendance] = useState(false);
  const [pasteRows, setPasteRows] = useState<BulkRow[]>([]);

  const rolesQ = useQuery({
    queryKey: ['roles'],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Role[]> => {
      const { data, error } = await supabase.from('roles').select('id,name').order('name');
      if (error) throw error;
      return (data ?? []) as Role[];
    },
  });

  const ressortsQ = useQuery({
    queryKey: ['ressorts'],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Ressort[]> => {
      const { data, error } = await supabase.from('ressorts').select('id,name').order('name');
      if (error) throw error;
      return (data ?? []) as Ressort[];
    },
  });

  const eventTypesQ = useQuery({
    queryKey: ['config', 'event_types'],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase
        .from('config')
        .select('value')
        .eq('key', 'event_types')
        .maybeSingle();
      if (error) throw error;
      const value = (data as { value?: unknown } | null)?.value;
      return parseJsonList(value);
    },
  });

  useEffect(() => {
    const rawIds = searchParams.get('eventIds') ?? searchParams.get('event_ids') ?? '';
    const ids = rawIds
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    const dateRaw = searchParams.get('date') ?? searchParams.get('event_day') ?? '';
    const parsedDate = dateRaw ? parseDateInput(dateRaw) : null;

    if (!ids.length && !parsedDate) return;

    if (ids.length) {
      setHighlightedEventIds(new Set(ids));
      setActiveTab('list');
    } else {
      setActiveTab('day');
    }
    if (parsedDate) {
      setDay(parsedDate);
      setRange(rangeForDate(parsedDate));
    }
  }, [searchParams]);

  const eventsQ = useQuery({
    queryKey: ['events', from.toISOString(), to.toISOString()],
    staleTime: 2 * 60 * 1000,
    queryFn: async (): Promise<EventRow[]> => {
      const { data, error } = await supabase
        .from('events')
        .select('id,title,event_type,start_time,end_time,description,location_name,requires_attendance_check')
        .gte('start_time', from.toISOString())
        .lte('start_time', to.toISOString())
        .order('start_time', { ascending: true });
      if (error) throw error;
      return (data ?? []) as EventRow[];
    },
  });

  const eventLinkNamesQ = useQuery({
    queryKey: ['event-link-names', from.toISOString(), to.toISOString()],
    enabled: (eventsQ.data ?? []).length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<{ rolesByEvent: Record<string, string[]>; ressortsByEvent: Record<string, string[]> }> => {
      const eventIds = (eventsQ.data ?? []).map((e) => e.id);
      if (!eventIds.length) return { rolesByEvent: {}, ressortsByEvent: {} };

      const roleNameById = new Map((rolesQ.data ?? []).map((r) => [r.id, r.name]));
      const ressortNameById = new Map((ressortsQ.data ?? []).map((r) => [r.id, r.name]));

      const [r1, r2] = await Promise.all([
        supabase.from('event_roles').select('event_id,role_id').in('event_id', eventIds),
        supabase.from('event_ressorts').select('event_id,ressort_id').in('event_id', eventIds),
      ]);
      if (r1.error) throw r1.error;
      if (r2.error) throw r2.error;

      const rolesByEvent: Record<string, string[]> = {};
      const ressortsByEvent: Record<string, string[]> = {};

      for (const row of (r1.data ?? []) as Array<{ event_id: string; role_id: string }>) {
        const name = roleNameById.get(String(row.role_id)) ?? String(row.role_id);
        const eid = String(row.event_id);
        if (!rolesByEvent[eid]) rolesByEvent[eid] = [];
        rolesByEvent[eid].push(name);
      }
      for (const row of (r2.data ?? []) as Array<{ event_id: string; ressort_id: string }>) {
        const name = ressortNameById.get(String(row.ressort_id)) ?? String(row.ressort_id);
        const eid = String(row.event_id);
        if (!ressortsByEvent[eid]) ressortsByEvent[eid] = [];
        ressortsByEvent[eid].push(name);
      }

      for (const k of Object.keys(rolesByEvent)) rolesByEvent[k].sort((a, b) => a.localeCompare(b));
      for (const k of Object.keys(ressortsByEvent)) ressortsByEvent[k].sort((a, b) => a.localeCompare(b));

      return { rolesByEvent, ressortsByEvent };
    },
  });

  const attendanceSummaryQ = useQuery({
    queryKey: ['attendance-summary', from.toISOString(), to.toISOString()],
    staleTime: 30_000,
    enabled:
      capabilities.canManageEvents &&
      (eventsQ.data ?? []).some((e) => e.requires_attendance_check) &&
      (eventsQ.data ?? []).length > 0,
    queryFn: async (): Promise<Record<string, AttendanceSummary>> => {
      const eventIds = (eventsQ.data ?? [])
        .filter((e) => e.requires_attendance_check)
        .map((e) => e.id);
      if (!eventIds.length) return {};

      const { data, error } = await supabase.rpc('get_event_attendance_overview', {
        p_event_ids: eventIds,
      });
      if (error) throw error;

      const out: Record<string, AttendanceSummary> = {};
      for (const row of (data ?? []) as AttendanceStatusRow[]) {
        const eid = String(row.event_id);
        const cur = out[eid] ?? { yes: 0, no: 0, abs: 0, unk: 0, late: 0, total: 0 };
        const s = normalizeStatus(String(row.status ?? ''));
        if (s === 'yes') {
          cur.yes += 1;
          if (formatLateArrivalTime(row.late_arrival_time)) cur.late += 1;
        }
        else if (s === 'no') cur.no += 1;
        else if (s === 'absence') cur.abs += 1;
        else cur.unk += 1;
        cur.total += 1;
        out[eid] = cur;
      }

      return out;
    },
  });

  const attendanceQ = useQuery({
    queryKey: ['attendance', attEvent?.id ?? 'none'],
    enabled: attOpen && !!attEvent?.id && capabilities.canManageEvents,
    staleTime: 30_000,
    queryFn: async (): Promise<AttendanceRow[]> => {
      const eid = attEvent!.id;
      const { data, error } = await supabase.rpc('get_event_attendance_overview', {
        p_event_ids: [eid],
      });
      if (error) throw error;
      return ((data ?? []) as AttendanceRow[]).sort((a, b) => {
        const aName = `${a.last_name ?? ''}|${a.first_name ?? ''}|${a.nickname ?? ''}`;
        const bName = `${b.last_name ?? ''}|${b.first_name ?? ''}|${b.nickname ?? ''}`;
        return aName.localeCompare(bName);
      });
    },
  });

  const undecidedRows = useMemo(() => {
    const rows = attendanceQ.data ?? [];
    return rows.filter((r) => normalizeStatus(r.status) === 'unknown');
  }, [attendanceQ.data]);

  const sendReminderM = useMutation({
    mutationFn: async () => {
      if (!attEvent?.id) throw new Error('Kein Termin ausgewaehlt');
      const title = reminderTitle.trim();
      const body = reminderBody.trim();
      if (!title || !body) throw new Error('Titel und Text muessen ausgefuellt sein.');

      const { data, error } = await supabase.functions.invoke('send-event-attendance-reminder', {
        body: { event_id: attEvent.id, title, body },
      });
      if (error) throw error;
      if (!data || data.ok !== true) {
        const msg = (data as { error?: string } | null)?.error ?? 'Unbekannter Fehler';
        throw new Error(msg);
      }
      return data as {
        recipients: number;
        users_with_tokens: number;
        tokens_total: number;
        sent_users: number;
        sent_tokens: number;
      };
    },
    onSuccess: async (data) => {
      const rec = Number(data?.recipients ?? 0);
      const sent = Number(data?.sent_users ?? 0);
      notifications.show({
        color: 'teal',
        title: 'Push gesendet',
        message: `Empfaenger: ${rec} · erreicht: ${sent}`,
      });
      setReminderOpen(false);
    },
    onError: (e: unknown) => {
      notifications.show({
        color: 'red',
        title: 'Push fehlgeschlagen',
        message: (e as { message?: string } | null)?.message ?? String(e),
      });
    },
  });

  const suggestReminderTextM = useMutation({
    mutationFn: async () => {
      if (!attEvent?.id) throw new Error('Kein Termin ausgewaehlt');
      const { data, error } = await supabase.functions.invoke('admin-ai', {
        body: {
          task: 'event_reminder_push_body',
          event_title: attEvent.title,
          start_time: attEvent.start_time,
          end_time: attEvent.end_time,
          undecided_count: undecidedRows.length,
        },
      });
      if (error) throw error;
      if (!data || data.ok !== true) {
        const msg = (data as { error?: string } | null)?.error ?? 'Unbekannter Fehler';
        throw new Error(msg);
      }
      return data as { body: string; fallback?: boolean };
    },
    onSuccess: (data) => {
      if (!attEvent) return;
      setReminderTitle((value) => value.trim() || defaultReminderTitle(attEvent));
      setReminderBody(data.body || defaultReminderBody(attEvent));
      notifications.show({
        color: data.fallback ? 'yellow' : 'teal',
        title: data.fallback ? 'Fallback-Text eingesetzt' : 'Text vorgeschlagen',
        message: data.fallback
          ? 'KI ist nicht verfuegbar. Ein sicherer Standardtext wurde eingesetzt.'
          : 'Der Vorschlag wurde in den Push-Text übernommen.',
      });
    },
    onError: (e: unknown) => {
      if (attEvent) {
        setReminderTitle((value) => value.trim() || defaultReminderTitle(attEvent));
        setReminderBody(defaultReminderBody(attEvent));
      }
      notifications.show({
        color: 'yellow',
        title: 'Textvorschlag nicht verfuegbar',
        message: (e as { message?: string } | null)?.message ?? String(e),
      });
    },
  });

  const linkQ = useQuery({
    queryKey: ['event-links', editingId ?? 'none'],
    enabled: modalOpen && !!editingId,
    queryFn: async (): Promise<{ role_ids: string[]; ressort_ids: string[] }> => {
      const eid = editingId!;
      const [r1, r2] = await Promise.all([
        supabase.from('event_roles').select('role_id').eq('event_id', eid),
        supabase.from('event_ressorts').select('ressort_id').eq('event_id', eid),
      ]);
      if (r1.error) throw r1.error;
      if (r2.error) throw r2.error;
      return {
        role_ids: ((r1.data ?? []) as Array<{ role_id: string }>).map((x) => String(x.role_id)),
        ressort_ids: ((r2.data ?? []) as Array<{ ressort_id: string }>).map((x) => String(x.ressort_id)),
      };
    },
  });

  async function sendEventChangeNotification(
    eventIds: string[],
    changeType: EventChangeType,
    options: EventChangeNotificationOptions = {},
  ) {
    const ids = Array.from(new Set(eventIds.map((id) => String(id ?? '').trim()).filter(Boolean)));
    if (!ids.length) throw new Error('event_id fehlt');
    const { data, error } = await supabase.functions.invoke('send-event-change-notification', {
      body: {
        event_ids: ids,
        change_type: changeType,
        delivery: 'digest',
        flush_after_seconds: 180,
        source: 'admin-web',
        ...options,
      },
    });
    if (error) throw error;
    if (!data || data.ok !== true) {
      const msg = (data as { error?: string } | null)?.error ?? 'Unbekannter Fehler';
      throw new Error(msg);
    }

    return data as EventChangeNotifyResult;
  }

  async function notifyEventChangeIfEnabled(
    eventId: string,
    changeType: EventChangeType,
    options: EventChangeNotificationOptions = {},
  ) {
    if (!notifyEventChange) return;
    return sendEventChangeNotification([eventId], changeType, options);
  }

  const createM = useMutation({
    mutationFn: async (payload: EventForm) => {
      if (!payload.event_type) throw new Error('event_type missing');
      if (!payload.start_time) throw new Error('start_time missing');
      if (!payload.end_time) throw new Error('end_time missing');
      if (!session?.user?.id) throw new Error('No session');

      const { data, error } = await supabase.rpc('create_event_with_links', {
        p_title: payload.title.trim(),
        p_event_type: payload.event_type,
        p_description: payload.description.trim(),
        p_start_time: payload.start_time.toISOString(),
        p_end_time: payload.end_time.toISOString(),
        p_location_name: payload.location_name.trim() || null,
        p_latitude: null,
        p_longitude: null,
        p_requires_attendance_check: payload.requires_attendance_check,
        p_role_ids: payload.role_ids,
        p_ressort_ids: payload.ressort_ids,
        p_created_by: session.user.id,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: async (createdEvent) => {
      notifications.show({ color: 'teal', title: 'Kalender', message: 'Termin gespeichert.' });
      if (notifyEventChange) {
        try {
          const createdId = String(
            (createdEvent as { id?: string } | null)?.id ??
              (Array.isArray(createdEvent) ? (createdEvent[0] as { id?: string } | undefined)?.id : '') ??
              '',
          ).trim();
          if (createdId) {
            const result = await notifyEventChangeIfEnabled(createdId, 'created');
            notifications.show({
              color: 'teal',
              title: result?.queued ? 'Push geplant' : 'Push gesendet',
              message: eventPushResultMessage(result),
            });
          } else {
            notifications.show({
              color: 'yellow',
              title: 'Push uebersprungen',
              message: 'Termin wurde gespeichert, aber event_id fehlt fuer Push.',
            });
          }
        } catch (e: unknown) {
          notifications.show({
            color: 'yellow',
            title: 'Termin gespeichert, Push fehlgeschlagen',
            message: (e as { message?: string } | null)?.message ?? String(e),
          });
        }
      }
      await qc.invalidateQueries({ queryKey: ['events'] });
      await qc.invalidateQueries({ queryKey: ['event-link-names'] });
      setModalOpen(false);
      setNotifyEventChange(false);
    },
    onError: (e: unknown) => {
      notifications.show({
        color: 'red',
        title: 'Speichern fehlgeschlagen',
        message: (e as { message?: string } | null)?.message ?? String(e),
      });
    },
  });

  const updateM = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: EventForm }) => {
      if (!payload.event_type) throw new Error('event_type missing');
      if (!payload.start_time) throw new Error('start_time missing');
      if (!payload.end_time) throw new Error('end_time missing');

      const { error: uErr } = await supabase
        .from('events')
        .update({
          title: payload.title.trim(),
          event_type: payload.event_type,
          description: payload.description.trim() || null,
          start_time: payload.start_time.toISOString(),
          end_time: payload.end_time.toISOString(),
          location_name: payload.location_name.trim() || null,
          location: payload.location_name.trim() || null,
          latitude: null,
          longitude: null,
          requires_attendance_check: payload.requires_attendance_check,
        })
        .eq('id', id);
      if (uErr) throw uErr;

      const [d1, d2] = await Promise.all([
        supabase.from('event_roles').delete().eq('event_id', id),
        supabase.from('event_ressorts').delete().eq('event_id', id),
      ]);
      if (d1.error) throw d1.error;
      if (d2.error) throw d2.error;

      if (payload.role_ids.length) {
        const { error } = await supabase.from('event_roles').insert(
          payload.role_ids.map((rid) => ({ event_id: id, role_id: rid })),
        );
        if (error) throw error;
      }

      if (payload.ressort_ids.length) {
        const { error } = await supabase.from('event_ressorts').insert(
          payload.ressort_ids.map((rsid) => ({ event_id: id, ressort_id: rsid })),
        );
        if (error) throw error;
      }
    },
    onSuccess: async (_, vars) => {
      notifications.show({ color: 'teal', title: 'Kalender', message: 'Termin aktualisiert.' });
      const previousEvent = eventsQ.data?.find((ev) => ev.id === vars.id) ?? null;
      const previousLocation = (previousEvent?.location_name ?? '').trim();
      const nextLocation = vars.payload.location_name.trim();
      const locationChanged = previousEvent != null && previousLocation !== nextLocation;
      if (notifyEventChange || locationChanged) {
        try {
          const result = await sendEventChangeNotification([vars.id], 'updated', {
            changed_fields: locationChanged ? ['location_name', 'location'] : [],
            urgent_location_only: locationChanged && !notifyEventChange,
            previous_events: previousEvent
              ? [{
                  id: vars.id,
                  location_name: previousEvent.location_name ?? null,
                  location: previousEvent.location_name ?? null,
                }]
              : [],
          });
          notifications.show({
            color: result?.skipped ? 'yellow' : 'teal',
            title: result?.skipped ? 'Push übersprungen' : result?.queued ? 'Push geplant' : 'Push gesendet',
            message: eventPushResultMessage(result),
          });
        } catch (e: unknown) {
          notifications.show({
            color: 'yellow',
            title: 'Termin aktualisiert, Push fehlgeschlagen',
            message: (e as { message?: string } | null)?.message ?? String(e),
          });
        }
      }
      await qc.invalidateQueries({ queryKey: ['events'] });
      await qc.invalidateQueries({ queryKey: ['event-link-names'] });
      setModalOpen(false);
      setNotifyEventChange(false);
    },
    onError: (e: unknown) => {
      notifications.show({
        color: 'red',
        title: 'Update fehlgeschlagen',
        message: (e as { message?: string } | null)?.message ?? String(e),
      });
    },
  });

  const deleteM = useMutation({
    mutationFn: async (id: string) => {
      const ok = window.confirm('Termin wirklich loeschen?');
      if (!ok) return;
      const { error } = await supabase.from('events').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['events'] });
      await qc.invalidateQueries({ queryKey: ['event-link-names'] });
      notifications.show({ color: 'teal', title: 'Kalender', message: 'Termin geloescht.' });
    },
    onError: (e: unknown) => {
      notifications.show({
        color: 'red',
        title: 'Loeschen fehlgeschlagen',
        message: (e as { message?: string } | null)?.message ?? String(e),
      });
    },
  });

  const bulkImportM = useMutation({
    mutationFn: async (rows: BulkRow[]) => {
      const rolesByName = new Map<string, string>();
      const ressortsByName = new Map<string, string>();
      for (const r of rolesQ.data ?? []) rolesByName.set(normalizeName(r.name), r.id);
      for (const r of ressortsQ.data ?? []) ressortsByName.set(normalizeName(r.name), r.id);

      const eventTypes = new Set((eventTypesQ.data ?? []).map(normalizeName));

      const next = [...rows];
      const createdIds: string[] = [];
      for (let i = 0; i < next.length; i++) {
        const row = next[i];
        row.result = '';
        const title = (row.title ?? '').trim();
        if (!title) continue;

        const type = normalizeName(row.event_type ?? '');
        if (!type || !eventTypes.has(type)) {
          row.result = 'Unbekannter Typ';
          continue;
        }

        const start = parseDateInput(row.start_time ?? '');
        if (!start) {
          row.result = 'Start ungültig';
          continue;
        }
        const end =
          parseDateInput(row.end_time ?? '') ??
          new Date(start.getTime() + 60 * 60 * 1000); // default 1h
        if (end.getTime() < start.getTime()) {
          row.result = 'Ende < Start';
          continue;
        }

        const roleIds: string[] = [];
        const ressortIds: string[] = [];

        for (const name of (row.roles ?? '').split(',').map((x) => x.trim()).filter(Boolean)) {
          const id = rolesByName.get(normalizeName(name));
          if (!id) {
            row.result = `Rolle fehlt: ${name}`;
            break;
          }
          roleIds.push(id);
        }
        if (row.result) continue;

        for (const name of (row.ressorts ?? '').split(',').map((x) => x.trim()).filter(Boolean)) {
          const id = ressortsByName.get(normalizeName(name));
          if (!id) {
            row.result = `Ressort fehlt: ${name}`;
            break;
          }
          ressortIds.push(id);
        }
        if (row.result) continue;

        try {
          const { data, error } = await supabase.rpc('create_event_with_links', {
            p_title: title,
            p_event_type: (row.event_type ?? '').trim(),
            p_description: (row.description ?? '').trim(),
            p_start_time: start.toISOString(),
            p_end_time: end.toISOString(),
            p_location_name: (row.location_name ?? '').trim() || null,
            p_latitude: null,
            p_longitude: null,
            p_requires_attendance_check: row.attendance,
            p_role_ids: roleIds,
            p_ressort_ids: ressortIds,
            p_created_by: session?.user?.id ?? null,
          });
          if (error) throw error;
          const createdId = String(
            (data as { id?: string } | null)?.id ??
              (Array.isArray(data) ? (data[0] as { id?: string } | undefined)?.id : '') ??
              '',
          ).trim();
          if (createdId) createdIds.push(createdId);
          row.result = 'OK';
        } catch (e: unknown) {
          row.result = (e as { message?: string } | null)?.message ?? 'Fehler';
        }
      }

      let notifyResult: EventChangeNotifyResult | null = null;
      let notifyError: string | null = null;
      if (bulkNotifyEventChange && createdIds.length) {
        try {
          notifyResult = await sendEventChangeNotification(createdIds, 'created');
        } catch (e: unknown) {
          notifyError = (e as { message?: string } | null)?.message ?? String(e);
        }
      }

      return { rows: next, createdIds, notifyResult, notifyError };
    },
    onSuccess: async (result) => {
      setBulkRows(result.rows);
      await qc.invalidateQueries({ queryKey: ['events'] });
      await qc.invalidateQueries({ queryKey: ['event-link-names'] });
      const pushSuffix = result.notifyResult?.queued
        ? ` Push geplant: ${Number(result.notifyResult.event_count ?? result.createdIds.length)} Termine werden gebündelt versendet.`
        : result.notifyResult
          ? ` Push: Empfaenger ${Number(result.notifyResult.recipients ?? 0)}, erreicht ${Number(result.notifyResult.sent_users ?? 0)}.`
          : '';
      notifications.show({
        color: result.notifyError ? 'yellow' : 'teal',
        title: 'Bulk',
        message: result.notifyError
          ? `Import fertig, Push fehlgeschlagen: ${result.notifyError}`
          : `Import fertig. Siehe Spalte "result".${pushSuffix}`,
      });
    },
    onError: (e: unknown) => {
      notifications.show({
        color: 'red',
        title: 'Bulk fehlgeschlagen',
        message: (e as { message?: string } | null)?.message ?? String(e),
      });
    },
  });

  const roleOptions = useMemo(
    () => (rolesQ.data ?? []).map((r) => ({ value: r.id, label: r.name })),
    [rolesQ.data],
  );
  const ressortOptions = useMemo(
    () => (ressortsQ.data ?? []).map((r) => ({ value: r.id, label: r.name })),
    [ressortsQ.data],
  );
  const eventTypeOptions = useMemo(
    () => (eventTypesQ.data ?? []).map((t) => ({ value: t, label: t })),
    [eventTypesQ.data],
  );

  const planDays = useMemo(() => {
    const items = (eventsQ.data ?? [])
      .map((ev) => {
        const start = new Date(ev.start_time);
        const end = ev.end_time ? new Date(ev.end_time) : new Date(start.getTime() + 60 * 60 * 1000);
        const key = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
        return { ev, start, end, key };
      })
      .sort((a, b) => a.start.getTime() - b.start.getTime());

    const map = new Map<string, Array<{ ev: EventRow; start: Date; end: Date }>>();
    for (const it of items) {
      const list = map.get(it.key) ?? [];
      list.push({ ev: it.ev, start: it.start, end: it.end });
      map.set(it.key, list);
    }

    return Array.from(map.entries())
      .map(([key, list]) => {
        const dt = list.length ? list[0].start : new Date(key);
        const label = dt.toLocaleDateString('de-DE', {
          weekday: 'short',
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
        return { key, label, date: dt, list };
      })
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [eventsQ.data]);

  const dayBlocks = useMemo((): { blocks: DayBlock[]; minHour: number; maxHour: number } => {
    const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, 0, 0, 0);
    const dayEnd = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 23, 59, 59, 999);
    const rows = (eventsQ.data ?? [])
      .map((ev) => {
        const start = new Date(ev.start_time);
        const end = ev.end_time ? new Date(ev.end_time) : new Date(start.getTime() + 60 * 60 * 1000);
        return { ev, start, end };
      })
      .filter(({ start }) => start >= dayStart && start <= dayEnd)
      .sort((a, b) => a.start.getTime() - b.start.getTime());

    const toMin = (d: Date) => d.getHours() * 60 + d.getMinutes();
    const input = rows.map(({ ev, start, end }) => ({
      id: ev.id,
      title: ev.title,
      event_type: ev.event_type,
      location_name: ev.location_name ?? null,
      start,
      end,
      startMin: toMin(start),
      endMin: Math.max(toMin(end), toMin(start) + 15), // minimum height (15 min)
    }));

    // Column assignment (interval partitioning).
    const active: Array<{ endMin: number; col: number }> = [];
    const freeCols: number[] = [];
    let nextCol = 0;
    const colById = new Map<string, number>();
    let maxCols = 0;

    function releaseCols(nowMin: number) {
      for (let i = active.length - 1; i >= 0; i--) {
        if (active[i].endMin <= nowMin) {
          freeCols.push(active[i].col);
          active.splice(i, 1);
        }
      }
    }

    for (const it of input) {
      releaseCols(it.startMin);
      freeCols.sort((a, b) => a - b);
      const col = freeCols.length ? (freeCols.shift() as number) : nextCol++;
      colById.set(it.id, col);
      active.push({ endMin: it.endMin, col });
      maxCols = Math.max(maxCols, nextCol);
    }

    // Overlap detection.
    const overlapsById = new Map<string, boolean>();
    for (let i = 0; i < input.length; i++) {
      const a = input[i];
      for (let j = i + 1; j < input.length; j++) {
        const b = input[j];
        if (b.startMin >= a.endMin) break;
        if (a.startMin < b.endMin && b.startMin < a.endMin) {
          overlapsById.set(a.id, true);
          overlapsById.set(b.id, true);
        }
      }
    }

    // Determine visible time range.
    let minHour = 8;
    let maxHour = 20;
    if (input.length) {
      const mins = input.map((x) => x.startMin);
      const maxs = input.map((x) => x.endMin);
      const minMin = Math.min(...mins);
      const maxMin = Math.max(...maxs);
      minHour = Math.max(0, Math.floor((minMin - 60) / 60));
      maxHour = Math.min(24, Math.ceil((maxMin + 60) / 60));
      if (maxHour <= minHour) maxHour = Math.min(24, minHour + 8);
    }

    const blocks: DayBlock[] = input.map((it) => ({
      ...it,
      col: colById.get(it.id) ?? 0,
      colCount: Math.max(1, maxCols),
      overlaps: overlapsById.get(it.id) === true,
    }));
    return { blocks, minHour, maxHour };
  }, [day, eventsQ.data]);

  function openCreate() {
    setModalMode('create');
    setEditingId(null);
    setForm({ ...emptyForm(), event_type: eventTypesQ.data?.[0] ?? null });
    setNotifyEventChange(false);
    setModalOpen(true);
  }

  function openEdit(ev: EventRow) {
    setModalMode('edit');
    setEditingId(ev.id);
    setForm({
      title: ev.title ?? '',
      event_type: ev.event_type ?? null,
      description: ev.description ?? '',
      start_time: new Date(ev.start_time),
      end_time: ev.end_time ? new Date(ev.end_time) : new Date(new Date(ev.start_time).getTime() + 60 * 60 * 1000),
      location_name: ev.location_name ?? '',
      requires_attendance_check: ev.requires_attendance_check ?? false,
      role_ids: [],
      ressort_ids: [],
    });
    setNotifyEventChange(false);
    setModalOpen(true);
  }

  function openDuplicate(ev: EventRow) {
    setModalMode('duplicate');
    setEditingId(null);
    const start = new Date(ev.start_time);
    const end = ev.end_time ? new Date(ev.end_time) : new Date(start.getTime() + 60 * 60 * 1000);
    setForm({
      title: ev.title ?? '',
      event_type: ev.event_type ?? null,
      description: ev.description ?? '',
      start_time: start,
      end_time: end,
      location_name: ev.location_name ?? '',
      requires_attendance_check: ev.requires_attendance_check ?? false,
      role_ids: [],
      ressort_ids: [],
    });
    setNotifyEventChange(false);
    setModalOpen(true);
  }

  // When link query resolves in edit mode, fill in role/ressort selections.
  useEffect(() => {
    if (!modalOpen || modalMode !== 'edit') return;
    if (!linkQ.data) return;
    setForm((f) => ({
      ...f,
      role_ids: linkQ.data.role_ids,
      ressort_ids: linkQ.data.ressort_ids,
    }));
  }, [linkQ.data, modalMode, modalOpen]);

  const busy = createM.isPending || updateM.isPending || deleteM.isPending;

  function parsePaste(notify = false) {
    const r = parseProbenplanPaste(pasteRaw, {
      defaultLocation: pasteDefaultLocation,
      defaultAttendance: pasteDefaultAttendance,
      eventTypes: eventTypesQ.data ?? [],
      knownRoles: (rolesQ.data ?? []).map((x) => x.name),
      knownRessorts: (ressortsQ.data ?? []).map((x) => x.name),
    });
    setPasteWarnings(r.warnings);
    setPasteDetected({
      dateCol: r.detected.dateCol,
      timeCol: r.detected.timeCol,
      lines: r.detected.lines,
      events: r.detected.events,
    });
    setPasteRows(r.rows);
    if (notify) notifications.show({ color: 'teal', title: 'Paste', message: `Erkannt: ${r.detected.events} Termin(e).` });
    return r;
  }

  function updatePasteRow(i: number, patch: Partial<BulkRow>) {
    setPasteRows((rows) =>
      rows.map((r, idx) => (idx === i ? { ...r, ...patch, result: patch.result ?? '' } : r)),
    );
  }

  const pastePreview = useMemo(() => {
    const items = (pasteRows ?? []).map((row, idx) => {
      const start = parseDateInput(row.start_time ?? '');
      const end =
        parseDateInput(row.end_time ?? '') ??
        (start ? new Date(start.getTime() + 60 * 60 * 1000) : null);
      const key = start ? dayjs(start).format('YYYY-MM-DD') : 'invalid';
      const sort = start ? start.getTime() : Number.MAX_SAFE_INTEGER;
      return { idx, row, start, end, key, sort };
    });

    items.sort((a, b) => a.sort - b.sort);

    const map = new Map<string, typeof items>();
    for (const it of items) {
      const list = map.get(it.key) ?? [];
      list.push(it);
      map.set(it.key, list);
    }

    const groups = Array.from(map.entries()).map(([key, list]) => {
      const label =
        key === 'invalid'
          ? 'Ungueltig / nicht erkannt'
          : new Date(`${key}T00:00:00`).toLocaleDateString('de-DE', {
              weekday: 'short',
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            });
      return { key, label, list };
    });

    groups.sort((a, b) => (a.key === 'invalid' ? 1 : 0) - (b.key === 'invalid' ? 1 : 0) || a.key.localeCompare(b.key));
    return groups;
  }, [pasteRows]);

  return (
    <Stack gap="md" className="contrast-page calendar-page">
      <PageHeader
        title="Kalender-Termine"
        description="Erstellen, bearbeiten, duplizieren. Bulk-Import wie Excel."
        loading={eventsQ.isLoading || rolesQ.isLoading || ressortsQ.isLoading}
        actions={
          <Group gap="xs">
            <Tooltip label="Neuer Termin" withArrow>
              <ActionIcon variant="light" color="brand" size="lg" onClick={openCreate}>
                <IconCalendarPlus size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Aktualisieren" withArrow>
              <ActionIcon
                variant="subtle"
                color="gray"
                className="contrast-icon-btn"
                size="lg"
                onClick={() => eventsQ.refetch()}
              >
                <IconRefresh size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        }
      />

      <Card
        className="contrast-surface"
        radius="lg"
        p="md"
        style={{
          background: 'var(--app-surface)',
          border: '1px solid var(--app-border)',
        }}
      >
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
          <DatePickerInput
            label="Von"
            value={from}
            onChange={(d) => {
              const dt = dateStringToDateLocal(d);
              if (!dt) return;
              setRange((r) => ({ ...r, from: dt }));
            }}
            clearable={false}
          />
          <DatePickerInput
            label="Bis"
            value={to}
            onChange={(d) => {
              const dt = dateStringToDateLocal(d);
              if (!dt) return;
              setRange((r) => ({ ...r, to: dt }));
            }}
            clearable={false}
          />
          <Group align="flex-end">
            <Button
              variant="light"
              color="gray"
              className="contrast-secondary-btn"
              onClick={() => setRange(defaultRange())}
            >
              Standard
            </Button>
          </Group>
        </SimpleGrid>
      </Card>

      <Tabs className="contrast-tabs" value={activeTab} onChange={setActiveTab} variant="outline" color="brand">
        <Tabs.List>
          <Tabs.Tab value="list">Liste</Tabs.Tab>
          <Tabs.Tab value="plan">Probenplan</Tabs.Tab>
          <Tabs.Tab value="paste">Paste</Tabs.Tab>
          <Tabs.Tab value="day">Tag</Tabs.Tab>
          <Tabs.Tab value="bulk">Bulk</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="list" pt="md">
          <Card
            className="contrast-surface"
            radius="lg"
            p="md"
            style={{
              background: 'var(--app-surface)',
              border: '1px solid var(--app-border)',
            }}
          >
            {(eventsQ.data ?? []).length === 0 && !eventsQ.isLoading ? (
              <EmptyState
                icon={<IconCalendarEvent size={48} />}
                title="Keine Termine im gewählten Zeitraum"
                description="Passe den Datumsbereich an oder lege einen neuen Termin an."
              />
            ) : (
              <ScrollArea h={520}>
                <Table className="contrast-table" highlightOnHover striped={false} withRowBorders={false}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Start</Table.Th>
                      <Table.Th>Titel</Table.Th>
                      <Table.Th>Typ</Table.Th>
                      <Table.Th>Ort</Table.Th>
                      <Table.Th>Anwesenheit</Table.Th>
                      <Table.Th style={{ width: 160 }} />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {(eventsQ.data ?? []).map((ev) => {
                      const highlighted = highlightedEventIds.has(ev.id);
                      return (
                      <Table.Tr
                        key={ev.id}
                        style={
                          highlighted
                            ? {
                                background: 'rgba(20, 184, 166, 0.14)',
                                outline: '1px solid rgba(20, 184, 166, 0.5)',
                              }
                            : undefined
                        }
                      >
                        <Table.Td>
                          <Text c="var(--color-text)" size="sm" fw={600}>
                            {new Date(ev.start_time).toLocaleString()}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text c="var(--color-text)" size="sm">
                            {ev.title}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge variant="light" color="brand">
                            {ev.event_type}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text c="dimmed" size="sm">
                            {ev.location_name ?? ''}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          {ev.requires_attendance_check ? (
                            attendanceSummaryQ.isFetching ? (
                              <Group gap="xs">
                                <Loader size="xs" />
                                <Text c="dimmed" size="sm">
                                  Lade…
                                </Text>
                              </Group>
                            ) : (
                              (() => {
                                const s = attendanceSummaryQ.data?.[ev.id];
                                const yes = s?.yes ?? 0;
                                const no = s?.no ?? 0;
                                const unk = s?.unk ?? 0;
                                const abs = s?.abs ?? 0;
                                const late = s?.late ?? 0;
                                const total = s?.total ?? yes + no + unk + abs;

                                return (
                                  <Group gap={6} wrap="nowrap">
                                    <Badge variant="light" color="teal" leftSection={<IconCheck size={12} />}>
                                      {yes}
                                    </Badge>
                                    {late > 0 && (
                                      <Badge variant="light" color="blue" leftSection={<IconClock size={12} />}>
                                        {late}
                                      </Badge>
                                    )}
                                    <Badge variant="light" color="red" leftSection={<IconX size={12} />}>
                                      {no}
                                    </Badge>
                                    <Badge className="contrast-badge-neutral" variant="light" color="gray" leftSection={<IconCircle size={12} />}>
                                      {unk}
                                    </Badge>
                                    {abs > 0 && (
                                      <Badge variant="light" color="yellow">
                                        Abw {abs}
                                      </Badge>
                                    )}
                                    <Badge className="contrast-badge-neutral" variant="light" color="gray">
                                      Σ {total}
                                    </Badge>
                                  </Group>
                                );
                              })()
                            )
                          ) : (
                            <Badge className="contrast-badge-neutral" variant="light" color="gray">
                              Kein Check
                            </Badge>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Group gap={6} justify="flex-end">
                            {ev.requires_attendance_check && capabilities.canManageEvents && (
                              <Tooltip label="Anwesenheit" withArrow>
                                <ActionIcon
                                  variant="subtle"
                                  color="teal"
                                  onClick={() => {
                                    setAttSearch('');
                                    setAttEvent(ev);
                                    setAttOpen(true);
                                  }}
                                >
                                  <IconUsers size={16} />
                                </ActionIcon>
                              </Tooltip>
                            )}
                            <Tooltip label="Duplizieren" withArrow>
                              <ActionIcon className="contrast-icon-btn" variant="subtle" color="gray" onClick={() => openDuplicate(ev)}>
                                <IconCopy size={16} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Bearbeiten" withArrow>
                              <ActionIcon className="contrast-icon-btn" variant="subtle" color="gray" onClick={() => openEdit(ev)}>
                                <IconEdit size={16} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Loeschen" withArrow>
                              <ActionIcon variant="subtle" color="red" onClick={() => deleteM.mutate(ev.id)}>
                                <IconTrash size={16} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    );
                    })}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            )}
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="plan" pt="md">
          <Card
            className="contrast-surface"
            radius="lg"
            p="md"
            style={{
              background: 'var(--app-surface)',
              border: '1px solid var(--app-border)',
            }}
          >
            <Group justify="space-between" align="flex-end" mb="sm">
              <Box>
                <Title order={4} c="var(--color-text)">
                  Probenplan (wie Excel)
                </Title>
                <Text c="dimmed">Nach Datum gruppiert, darunter Uhrzeiten. Rollen/Ressorts sofort sichtbar.</Text>
              </Box>
              <Group gap="sm" align="center">
                {(eventsQ.isFetching || eventLinkNamesQ.isFetching) && <Loader size="sm" />}
                <Button variant="light" color="gray" className="contrast-secondary-btn" onClick={() => eventsQ.refetch()}>
                  Refresh
                </Button>
              </Group>
            </Group>

            {planDays.length === 0 ? (
              <Text c="dimmed">Aktuell keine Termine im gewählten Zeitraum.</Text>
            ) : (
              <ScrollArea h={680} offsetScrollbars>
                <Stack gap="sm">
                  {planDays.map((d) => (
                    <Paper
                      key={d.key}
                      radius="lg"
                      p="md"
                      style={{
                        background: 'var(--app-surface-2)',
                        border: '1px solid var(--app-border-2)',
                      }}
                    >
                      <Group justify="space-between" align="flex-start" mb="xs">
                        <Text c="var(--color-text)" fw={800}>
                          {d.label}
                        </Text>
                        <Badge className="contrast-badge-neutral" variant="light" color="gray">
                          {d.list.length} Termin(e)
                        </Badge>
                      </Group>

                      <Table className="contrast-table" withRowBorders={false} highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th style={{ width: 140 }}>Zeit</Table.Th>
                            <Table.Th>Titel / Details</Table.Th>
                            <Table.Th style={{ width: 320 }}>Rollen / Ressorts</Table.Th>
                            <Table.Th style={{ width: 180 }} />
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {d.list.map(({ ev, start, end }) => {
                            const startLbl = start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                            const endLbl = end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                            const roles = eventLinkNamesQ.data?.rolesByEvent?.[ev.id] ?? [];
                            const ressorts = eventLinkNamesQ.data?.ressortsByEvent?.[ev.id] ?? [];
                            const s = attendanceSummaryQ.data?.[ev.id];
                            const accent = eventTypeAccent(ev.event_type);

                            const showRoles = roles.slice(0, 8);
                            const roleMore = Math.max(0, roles.length - showRoles.length);
                            const showRessorts = ressorts.slice(0, 4);
                            const ressortMore = Math.max(0, ressorts.length - showRessorts.length);

                            return (
                              <Table.Tr key={ev.id}>
                                <Table.Td>
                                  <Box style={{ borderLeft: `4px solid ${accent}`, paddingLeft: 10 }}>
                                    <Text c="var(--color-text)" fw={700} size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                      {startLbl} - {endLbl}
                                    </Text>
                                    <Text c="dimmed" size="xs">
                                      {ev.event_type}
                                    </Text>
                                  </Box>
                                </Table.Td>
                                <Table.Td>
                                  <Text c="var(--color-text)" fw={700} size="sm">
                                    {ev.title}
                                  </Text>
                                  {(ev.location_name || ev.description) && (
                                    <Text c="dimmed" size="xs" style={{ whiteSpace: 'pre-wrap' }}>
                                      {ev.location_name ? `${ev.location_name}` : ''}
                                      {ev.location_name && ev.description ? ' · ' : ''}
                                      {ev.description ?? ''}
                                    </Text>
                                  )}
                                </Table.Td>
                                <Table.Td>
                                  <Group gap={6}>
                                    {showRoles.map((r) => (
                                      <Badge key={`r-${ev.id}-${r}`} variant="light" color="brand">
                                        {r}
                                      </Badge>
                                    ))}
                                    {roleMore > 0 && (
                                      <Badge className="contrast-badge-neutral" variant="light" color="gray">
                                        +{roleMore}
                                      </Badge>
                                    )}
                                  </Group>
                                  {ressorts.length > 0 && (
                                    <Group gap={6} mt={6}>
                                      {showRessorts.map((r) => (
                                        <Badge key={`rs-${ev.id}-${r}`} variant="light" color="grape">
                                          {r}
                                        </Badge>
                                      ))}
                                      {ressortMore > 0 && (
                                        <Badge className="contrast-badge-neutral" variant="light" color="gray">
                                          +{ressortMore}
                                        </Badge>
                                      )}
                                    </Group>
                                  )}
                                  {ev.requires_attendance_check && capabilities.canManageEvents && (
                                    <Group gap={6} mt={8}>
                                      <Badge variant="light" color="teal" leftSection={<IconCheck size={12} />}>
                                        {s?.yes ?? 0}
                                      </Badge>
                                      {(s?.late ?? 0) > 0 && (
                                        <Badge variant="light" color="blue" leftSection={<IconClock size={12} />}>
                                          {s?.late ?? 0}
                                        </Badge>
                                      )}
                                      <Badge variant="light" color="red" leftSection={<IconX size={12} />}>
                                        {s?.no ?? 0}
                                      </Badge>
                                      <Badge className="contrast-badge-neutral" variant="light" color="gray" leftSection={<IconCircle size={12} />}>
                                        {s?.unk ?? 0}
                                      </Badge>
                                      {s?.abs ? (
                                        <Badge variant="light" color="yellow">
                                          Abw {s.abs}
                                        </Badge>
                                      ) : null}
                                    </Group>
                                  )}
                                </Table.Td>
                                <Table.Td>
                                  <Group gap={6} justify="flex-end">
                                    {ev.requires_attendance_check && capabilities.canManageEvents && (
                                      <Tooltip label="Anwesenheit" withArrow>
                                        <ActionIcon
                                          variant="subtle"
                                          color="teal"
                                          onClick={() => {
                                            setAttSearch('');
                                            setAttEvent(ev);
                                            setAttOpen(true);
                                          }}
                                        >
                                          <IconUsers size={16} />
                                        </ActionIcon>
                                      </Tooltip>
                                    )}
                                    <Tooltip label="Duplizieren" withArrow>
                                      <ActionIcon className="contrast-icon-btn" variant="subtle" color="gray" onClick={() => openDuplicate(ev)}>
                                        <IconCopy size={16} />
                                      </ActionIcon>
                                    </Tooltip>
                                    <Tooltip label="Bearbeiten" withArrow>
                                      <ActionIcon className="contrast-icon-btn" variant="subtle" color="gray" onClick={() => openEdit(ev)}>
                                        <IconEdit size={16} />
                                      </ActionIcon>
                                    </Tooltip>
                                    <Tooltip label="Loeschen" withArrow>
                                      <ActionIcon variant="subtle" color="red" onClick={() => deleteM.mutate(ev.id)}>
                                        <IconTrash size={16} />
                                      </ActionIcon>
                                    </Tooltip>
                                  </Group>
                                </Table.Td>
                              </Table.Tr>
                            );
                          })}
                        </Table.Tbody>
                      </Table>
                    </Paper>
                  ))}
                </Stack>
              </ScrollArea>
            )}
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="paste" pt="md">
          <Card
            className="contrast-surface"
            radius="lg"
            p="md"
            style={{
              background: 'var(--app-surface)',
              border: '1px solid var(--app-border)',
            }}
          >
            <Stack gap="sm">
              <Group justify="space-between" align="flex-end">
                <Box>
                  <Title order={4} c="var(--color-text)">
                    Paste-Import (Excel)
                  </Title>
                  <Text c="dimmed">
                    Excel-Bereich kopieren (Tabs + Zeilen) und hier einfuegen. Datum wird automatisch mitgezogen (merged cells ok).
                  </Text>
                </Box>
                {(rolesQ.isLoading || ressortsQ.isLoading || eventTypesQ.isLoading) && <Loader size="sm" />}
              </Group>

              <Textarea
                label="Aus Excel einfuegen"
                placeholder={'Beispiel:\\n31.01.2026\\t10 Uhr - 13 Uhr\\tTanz mit Linda:\\t...\\tSam, ...'}
                autosize
                minRows={8}
                value={pasteRaw}
                onChange={(e) => setPasteRaw(e.currentTarget.value)}
              />

              <Group align="flex-end" grow>
                <Select
                  label="Ort (Standard)"
                  placeholder="Optional"
                  data={predefinedLocations}
                  value={pasteDefaultLocation}
                  onChange={(v) => setPasteDefaultLocation(v ?? null)}
                  searchable
                  clearable
                  nothingFoundMessage="Kein Treffer"
                />
                <Switch
                  label="Anwesenheits-Check (Standard)"
                  checked={pasteDefaultAttendance}
                  onChange={(e) => setPasteDefaultAttendance(e.currentTarget.checked)}
                />
              </Group>

              <Group justify="flex-end">
                <Button
                  variant="light"
                  color="gray"
                  className="contrast-secondary-btn"
                  disabled={!pasteRaw.trim() || rolesQ.isLoading || ressortsQ.isLoading || eventTypesQ.isLoading}
                  onClick={() => {
                    parsePaste(true);
                  }}
                >
                  Parsen
                </Button>
                <Button
                  disabled={!pasteRaw.trim() || rolesQ.isLoading || ressortsQ.isLoading || eventTypesQ.isLoading}
                  onClick={() => {
                    const rows = pasteRows.length ? pasteRows : parsePaste(false).rows;
                    const padded = padBulkRows(rows);
                    setBulkRows(padded);
                    setActiveTab('bulk');
                  }}
                >
                  In Bulk uebernehmen
                </Button>
                <Button
                  color="brand"
                  loading={bulkImportM.isPending}
                  disabled={
                    busy ||
                    bulkImportM.isPending ||
                    pasteRows.length === 0 ||
                    rolesQ.isLoading ||
                    ressortsQ.isLoading ||
                    eventTypesQ.isLoading
                  }
                  onClick={() => {
                    const toImport = pasteRows.map((r) => ({ ...r }));
                    bulkImportM.mutate(toImport, {
                      onSuccess: (result) => setPasteRows(result.rows),
                    });
                  }}
                >
                  Importieren
                </Button>
              </Group>

              {pasteDetected && (
                <Text c="dimmed" size="sm">
                  Erkannt: {pasteDetected.events} Termin(e) aus {pasteDetected.lines} Zeile(n). Spalten: Datum={pasteDetected.dateCol + 1}, Zeit={pasteDetected.timeCol + 1}.
                </Text>
              )}

              {pasteWarnings.length > 0 && (
                <Card
                  radius="md"
                  p="sm"
                  style={{
                    background: 'var(--app-surface-2)',
                    border: '1px solid var(--app-border-2)',
                  }}
                >
                  <Text c="var(--color-text)" fw={700} size="sm" mb={6}>
                    Hinweise
                  </Text>
                  <Stack gap={4}>
                    {pasteWarnings.slice(0, 8).map((w, i) => (
                      <Text key={i} c="dimmed" size="sm">
                        {w}
                      </Text>
                    ))}
                    {pasteWarnings.length > 8 && (
                      <Text c="dimmed" size="sm">
                        … +{pasteWarnings.length - 8} weitere
                      </Text>
                    )}
                  </Stack>
                </Card>
              )}

              {pasteRows.length > 0 && (
                <Card
                  radius="md"
                  p="sm"
                  style={{
                    background: 'var(--app-surface-2)',
                    border: '1px solid var(--app-border-2)',
                  }}
                >
                  <Group justify="space-between" align="flex-end" mb="xs">
                    <Box>
                      <Text c="var(--color-text)" fw={800}>
                        Vorschau (editierbar)
                      </Text>
                      <Text c="dimmed" size="sm">
                        Passe Rollen/Ressorts, Zeiten und Details an und importiere dann. Fehler werden rot markiert.
                      </Text>
                    </Box>
                    <Group gap="xs">
                      <Button
                        variant="light"
                        color="gray"
                        className="contrast-secondary-btn"
                        onClick={() => {
                          setPasteRows([]);
                          setPasteWarnings([]);
                          setPasteDetected(null);
                        }}
                      >
                        Leeren
                      </Button>
                      <Button
                        color="brand"
                        loading={bulkImportM.isPending}
                        disabled={busy || bulkImportM.isPending || pasteRows.length === 0}
                        onClick={() => {
                          const toImport = pasteRows.map((r) => ({ ...r }));
                          bulkImportM.mutate(toImport, {
                            onSuccess: (result) => setPasteRows(result.rows),
                          });
                        }}
                      >
                        Importieren
                      </Button>
                    </Group>
                  </Group>

                  <ScrollArea h={680} offsetScrollbars>
                    <Stack gap="sm">
                      {pastePreview.map((g) => (
                        <Paper
                          key={g.key}
                          radius="lg"
                          p="md"
                          style={{
                            background: 'var(--app-surface)',
                            border: '1px solid var(--app-border)',
                          }}
                        >
                          <Group justify="space-between" align="flex-start" mb="xs">
                            <Text c="var(--color-text)" fw={800}>
                              {g.label}
                            </Text>
                            <Badge className="contrast-badge-neutral" variant="light" color="gray">
                              {g.list.length} Termin(e)
                            </Badge>
                          </Group>

                          <Stack gap="sm">
                            {g.list.map((it) => {
                              const { idx, row, start, end } = it;
                              const accent = eventTypeAccent(row.event_type ?? '');
                              const status = previewStatusForRow(row, {
                                eventTypes: eventTypesQ.data ?? [],
                                roleNames: (rolesQ.data ?? []).map((x) => x.name),
                                ressortNames: (ressortsQ.data ?? []).map((x) => x.name),
                              });
                              const statusColor = status.level === 'ok' ? 'teal' : status.level === 'warn' ? 'yellow' : 'red';

                              const startLbl = start ? start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '??';
                              const endLbl = end ? end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '??';

                              return (
                                <Card
                                  key={`p-${g.key}-${idx}`}
                                  radius="md"
                                  p="md"
                                  style={{
                                    background: 'var(--app-surface-2)',
                                    border: '1px solid var(--app-border)',
                                  }}
                                >
                                  <Group justify="space-between" align="flex-start" mb="xs">
                                    <Box style={{ borderLeft: `4px solid ${accent}`, paddingLeft: 10 }}>
                                      <Text c="var(--color-text)" fw={800} style={{ fontVariantNumeric: 'tabular-nums' }}>
                                        {startLbl} - {endLbl}
                                      </Text>
                                      <Text c="dimmed" size="sm">
                                        {row.location_name ?? ''}
                                      </Text>
                                    </Box>
                                    <Group gap="xs">
                                      <Badge variant="light" color="brand">
                                        {(row.event_type ?? '').trim() || 'Typ?'}
                                      </Badge>
                                      <Badge variant="light" color={statusColor}>
                                        {status.label}
                                      </Badge>
                                      <Tooltip label="Aus Vorschau entfernen" withArrow>
                                        <ActionIcon
                                          variant="subtle"
                                          color="red"
                                          onClick={() => setPasteRows((rows) => rows.filter((_, j) => j !== idx))}
                                        >
                                          <IconTrash size={16} />
                                        </ActionIcon>
                                      </Tooltip>
                                    </Group>
                                  </Group>

                                  {status.message ? (
                                    <Text c={status.level === 'error' ? 'red.2' : 'yellow.2'} size="sm" mb="xs">
                                      {status.message}
                                    </Text>
                                  ) : null}

                                  <Stack gap="sm">
                                    <Group grow align="flex-end">
                                      <TextInput
                                        label="Titel"
                                        value={row.title ?? ''}
                                        onChange={(e) => updatePasteRow(idx, { title: e.currentTarget.value })}
                                      />
                                      <Select
                                        label="Typ"
                                        placeholder="Bitte waehlen"
                                        data={eventTypeOptions}
                                        value={(row.event_type ?? '').trim() || null}
                                        onChange={(v) => updatePasteRow(idx, { event_type: v ?? '' })}
                                        searchable
                                        clearable
                                        nothingFoundMessage="Kein Treffer"
                                      />
                                    </Group>

                                    <Group grow align="flex-end">
                                      <TextInput
                                        label="Start"
                                        placeholder="z.B. 31.01.2026 10:00"
                                        value={row.start_time ?? ''}
                                        onChange={(e) => updatePasteRow(idx, { start_time: e.currentTarget.value })}
                                      />
                                      <TextInput
                                        label="Ende"
                                        placeholder="leer = +1h"
                                        value={row.end_time ?? ''}
                                        onChange={(e) => updatePasteRow(idx, { end_time: e.currentTarget.value })}
                                      />
                                    </Group>

                                    <Group grow align="flex-end">
                                      <TextInput
                                        label="Ort"
                                        value={row.location_name ?? ''}
                                        onChange={(e) => updatePasteRow(idx, { location_name: e.currentTarget.value })}
                                      />
                                      <Switch
                                        label="Anwesenheits-Check"
                                        checked={row.attendance}
                                        onChange={(e) => updatePasteRow(idx, { attendance: e.currentTarget.checked })}
                                      />
                                    </Group>

                                    <MultiSelect
                                      label="Rollen"
                                      placeholder="Optional"
                                      data={(rolesQ.data ?? []).map((x) => x.name)}
                                      value={splitCommaList(row.roles)}
                                      onChange={(v) => updatePasteRow(idx, { roles: joinCommaList(v) })}
                                      searchable
                                      clearable
                                      nothingFoundMessage="Keine Rolle gefunden"
                                    />

                                    <MultiSelect
                                      label="Ressorts"
                                      placeholder="Optional"
                                      data={(ressortsQ.data ?? []).map((x) => x.name)}
                                      value={splitCommaList(row.ressorts)}
                                      onChange={(v) => updatePasteRow(idx, { ressorts: joinCommaList(v) })}
                                      searchable
                                      clearable
                                      nothingFoundMessage="Kein Ressort gefunden"
                                    />

                                    <Textarea
                                      label="Beschreibung / Notizen"
                                      autosize
                                      minRows={2}
                                      value={row.description ?? ''}
                                      onChange={(e) => updatePasteRow(idx, { description: e.currentTarget.value })}
                                    />
                                  </Stack>
                                </Card>
                              );
                            })}
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </ScrollArea>
                </Card>
              )}
            </Stack>
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="day" pt="md">
          <Card
            className="contrast-surface"
            radius="lg"
            p="md"
            style={{
              background: 'var(--app-surface)',
              border: '1px solid var(--app-border)',
            }}
          >
            <Stack gap="sm">
              <Group justify="space-between" align="flex-end">
                <Box>
                  <Title order={4} c="var(--color-text)">
                    Tagesansicht
                  </Title>
                  <Text c="dimmed">
                    Overlaps werden markiert. Ueberlappungen sind erlaubt (z.B. zeitgleiche Proben).
                  </Text>
                </Box>
                <Group gap="sm" align="flex-end">
                  <DatePickerInput
                    label="Tag"
                    value={day}
                    onChange={(d) => {
                      const dt = dateStringToDateLocal(d);
                      if (!dt) return;
                      setDay(dt);
                    }}
                    clearable={false}
                  />
                  <Button
                    variant="light"
                    color="gray"
                    className="contrast-secondary-btn"
                    onClick={() => setDay(new Date())}
                  >
                    Heute
                  </Button>
                </Group>
              </Group>

              {dayBlocks.blocks.length === 0 ? (
                <Text c="dimmed">Aktuell keine Termine an diesem Tag.</Text>
              ) : (
                <ScrollArea h={640} offsetScrollbars>
                  <Box
                    style={{
                      position: 'relative',
                      borderRadius: 14,
                      border: '1px solid var(--app-border)',
                      background: 'var(--app-surface-2)',
                      padding: 12,
                    }}
                  >
                    {(() => {
                      const pxPerMin = 2; // 120px / hour
                      const minHour = dayBlocks.minHour;
                      const maxHour = dayBlocks.maxHour;
                      const startMin = minHour * 60;
                      const endMin = maxHour * 60;
                      const height = Math.max(240, (endMin - startMin) * pxPerMin);

                      return (
                        <Box style={{ position: 'relative', height }}>
                          {/* hour lines */}
                          {Array.from({ length: Math.max(1, maxHour - minHour + 1) }).map((_, i) => {
                            const h = minHour + i;
                            const top = (h * 60 - startMin) * pxPerMin;
                            return (
                              <Box
                                key={`h-${h}`}
                                style={{
                                  position: 'absolute',
                                  left: 0,
                                  right: 0,
                                  top,
                                  height: 1,
                                  background: 'var(--app-border)',
                                }}
                              >
                                <Text
                                  size="xs"
                                  c="dimmed"
                                  style={{
                                    position: 'absolute',
                                    left: 0,
                                    top: -10,
                                    fontVariantNumeric: 'tabular-nums',
                                  }}
                                >
                                  {String(h).padStart(2, '0')}:00
                                </Text>
                              </Box>
                            );
                          })}

                          {/* blocks */}
                          {dayBlocks.blocks.map((b) => {
                            const top = (b.startMin - startMin) * pxPerMin;
                            const h = Math.max(18, (b.endMin - b.startMin) * pxPerMin);
                            const gap = 8;
                            const colW = 100 / b.colCount;
                            const leftPct = b.col * colW;
                            const widthPct = colW;
                            const startLbl = b.start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                            const endLbl = b.end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

                            return (
                              <Box
                                key={b.id}
                                style={{
                                  position: 'absolute',
                                  top,
                                  left: `calc(${leftPct}% + ${gap / 2}px)`,
                                  width: `calc(${widthPct}% - ${gap}px)`,
                                  height: h,
                                  borderRadius: 12,
                                  border: b.overlaps
                                    ? '1px solid var(--color-danger)'
                                    : '1px solid var(--app-border)',
                                  background: b.overlaps
                                    ? 'linear-gradient(180deg, rgba(212, 63, 87, 0.28), var(--app-surface-3))'
                                    : 'linear-gradient(180deg, rgba(69, 61, 181, 0.28), var(--app-surface-3))',
                                  padding: '10px 10px',
                                  overflow: 'hidden',
                                  cursor: 'pointer',
                                }}
                                onClick={() => {
                                  const ev = (eventsQ.data ?? []).find((x) => x.id === b.id);
                                  if (ev) openEdit(ev);
                                }}
                              >
                                <Group justify="space-between" gap="xs" wrap="nowrap">
                                  <Text c="var(--color-text)" fw={700} size="sm" lineClamp={1}>
                                    {b.title}
                                  </Text>
                                  <Badge variant="light" color={b.overlaps ? 'red' : 'brand'}>
                                    {b.event_type}
                                  </Badge>
                                </Group>
                                <Text size="xs" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {startLbl} - {endLbl}
                                  {b.location_name ? ` · ${b.location_name}` : ''}
                                </Text>
                                {b.overlaps ? (
                                  <Text size="xs" c="red.2" mt={6}>
                                    Ueberlappt mit anderem Termin
                                  </Text>
                                ) : null}
                              </Box>
                            );
                          })}
                        </Box>
                      );
                    })()}
                  </Box>
                </ScrollArea>
              )}
            </Stack>
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="bulk" pt="md">
          <Card
            className="contrast-surface"
            radius="lg"
            p="md"
            style={{
              background: 'var(--app-surface)',
              border: '1px solid var(--app-border)',
            }}
          >
            <Stack gap="sm">
              <Text c="dimmed">
                Tipp: Du kannst direkt aus Excel reinkopieren. Zeitformate werden automatisch erkannt, z.B.
                <Text span c="var(--color-text)"> 2026-02-14 19:30</Text> oder <Text span c="var(--color-text)">14.02.2026 19:30</Text>.
                Ende kann leer bleiben (Standard: +1 Stunde).
              </Text>
              <Switch
                label="Ein Push nach dem Bulk-Import senden"
                description="Alle erfolgreich importierten Termine werden zu einer Sammelmeldung zusammengefasst."
                checked={bulkNotifyEventChange}
                onChange={(e) => setBulkNotifyEventChange(e.currentTarget.checked)}
              />
              <Divider />
              <Box style={{ height: 420 }}>
                <DataSheetGrid<BulkRow>
                  value={bulkRows}
                  onChange={(value) => setBulkRows(value)}
                  columns={[
                    { ...keyColumn('title', textColumn), title: 'Titel' },
                    { ...keyColumn('event_type', textColumn), title: 'Typ' },
                    { ...keyColumn('start_time', textColumn), title: 'Start' },
                    { ...keyColumn('end_time', textColumn), title: 'Ende' },
                    { ...keyColumn('location_name', textColumn), title: 'Ort' },
                    { ...keyColumn('description', textColumn), title: 'Beschreibung' },
                    { ...keyColumn('roles', textColumn), title: 'Rollen (Komma)' },
                    { ...keyColumn('ressorts', textColumn), title: 'Ressorts (Komma)' },
                    { ...keyColumn('attendance', checkboxColumn), title: 'Check' },
                    { ...keyColumn('result', textColumn), title: 'result' },
                  ]}
                  rowHeight={36}
                  addRowsComponent={false}
                />
              </Box>
              <Group justify="flex-end">
                <Button
                  onClick={() => bulkImportM.mutate(bulkRows)}
                  loading={bulkImportM.isPending}
                  disabled={busy || rolesQ.isLoading || ressortsQ.isLoading || eventTypesQ.isLoading}
                >
                  Import starten
                </Button>
              </Group>
            </Stack>
          </Card>
        </Tabs.Panel>
      </Tabs>

      <Modal
        opened={attOpen}
        onClose={() => setAttOpen(false)}
        title="Anwesenheit"
        centered
        size="lg"
        overlayProps={{ blur: 2 }}
      >
        {!capabilities.canManageEvents ? (
          <Text c="dimmed">Keine Berechtigung (manage_events) fuer diese Ansicht.</Text>
        ) : !attEvent ? (
          <Text c="dimmed">Kein Termin ausgewaehlt.</Text>
        ) : (
          <Stack gap="sm">
            <Box>
              <Text c="var(--color-text)" fw={800}>
                {attEvent.title}
              </Text>
              <Text c="dimmed" size="sm">
                {new Date(attEvent.start_time).toLocaleString()}
              </Text>
            </Box>

            <TextInput
              label="Suchen"
              placeholder="Name..."
              value={attSearch}
              onChange={(e) => setAttSearch(e.currentTarget.value)}
            />

            {attendanceQ.isLoading ? (
              <Group gap="xs">
                <Loader size="sm" />
                <Text c="dimmed">Lade Anwesenheit…</Text>
              </Group>
            ) : attendanceQ.isError ? (
              <Text c="dimmed">Konnte Anwesenheit nicht laden (keine Rechte oder View gesperrt).</Text>
            ) : (
              (() => {
                const rows = attendanceQ.data ?? [];

                let yes = 0;
                let no = 0;
                let abs = 0;
                let unk = 0;
                for (const r of rows) {
                  const s = normalizeStatus(r.status);
                  if (s === 'yes') yes++;
                  else if (s === 'no') no++;
                  else if (s === 'absence') abs++;
                  else unk++;
                }

                const filtered = rows.filter((r) => {
                  if (!attSearch.trim()) return true;
                  const q = attSearch.toLowerCase();
                  return nameLabel(r).toLowerCase().includes(q);
                });

                return (
                  <Stack gap="sm">
                    <Group gap="xs">
                      <Badge variant="light" color="teal">
                        Zugesagt: {yes}
                      </Badge>
                      <Badge variant="light" color="red">
                        Abgesagt: {no}
                      </Badge>
                      <Badge className="contrast-badge-neutral" variant="light" color="gray">
                        Keine Rueckmeldung: {unk}
                      </Badge>
                      <Badge variant="light" color="yellow">
                        Abwesenheit: {abs}
                      </Badge>
                    </Group>

                    <Group justify="space-between" align="center">
                      <Text c="dimmed" size="sm">
                        Offene Rueckmeldungen: {unk}
                      </Text>
                      <Button
                        leftSection={<IconBellRinging size={16} />}
                        variant="light"
                        color="brand"
                        disabled={unk === 0}
                        loading={sendReminderM.isPending}
                        onClick={() => {
                          setReminderTitle(defaultReminderTitle(attEvent));
                          setReminderBody(defaultReminderBody(attEvent));
                          setReminderOpen(true);
                        }}
                      >
                        Offene erinnern
                      </Button>
                    </Group>

                    <ScrollArea h={420} offsetScrollbars>
                      <Stack gap="xs">
                        {filtered.map((r) => {
                          const s = normalizeStatus(r.status);
                          const color =
                            s === 'yes' ? 'teal' : s === 'no' ? 'red' : s === 'absence' ? 'yellow' : 'gray';
                          const lateArrival = s === 'yes' ? formatLateArrivalTime(r.late_arrival_time) : '';
                          const label = s === 'yes'
                            ? (lateArrival ? `Zugesagt ab ${lateArrival}` : 'Zugesagt')
                            : s === 'no'
                              ? 'Abgesagt'
                              : s === 'absence'
                                ? 'Abwesenheit'
                                : 'Offen';
                          const when = r.attendance_created_at ? new Date(r.attendance_created_at).toLocaleString() : '';

                          return (
                            <Card
                              key={r.user_id}
                              radius="md"
                              p="sm"
                              style={{
                                background: 'var(--app-surface-3)',
                                border: '1px solid var(--app-border-2)',
                              }}
                            >
                              <Group justify="space-between" align="center">
                                <Group gap="sm" wrap="nowrap">
                                  <Avatar src={r.avatar_url ?? undefined} radius="xl" />
                                  <Box>
                                    <Text c="var(--color-text)" fw={700} size="sm">
                                      {nameLabel(r)}
                                    </Text>
                                    {when && (
                                      <Text c="dimmed" size="xs">
                                        {when}
                                      </Text>
                                    )}
                                    {lateArrival && (
                                      <Text c="blue" size="xs" fw={700}>
                                        Kommt ab {lateArrival}
                                      </Text>
                                    )}
                                  </Box>
                                </Group>
                                <Badge variant="light" color={color}>
                                  {label}
                                </Badge>
                              </Group>
                            </Card>
                          );
                        })}
                      </Stack>
                    </ScrollArea>
                  </Stack>
                );
              })()
            )}
          </Stack>
        )}
      </Modal>

      <Modal
        opened={reminderOpen}
        onClose={() => setReminderOpen(false)}
        title="Push-Erinnerung senden"
        centered
        size="lg"
        overlayProps={{ blur: 2 }}
      >
        {!attEvent ? (
          <Text c="dimmed">Kein Termin ausgewaehlt.</Text>
        ) : (
          <Stack gap="sm">
            <Box>
              <Text c="var(--color-text)" fw={800}>
                {attEvent.title}
              </Text>
              <Text c="dimmed" size="sm">
                Empfaenger (offen): {undecidedRows.length}
              </Text>
            </Box>

            <TextInput
              label="Titel"
              value={reminderTitle}
              onChange={(e) => setReminderTitle(e.currentTarget.value)}
            />
            <Textarea
              label="Text"
              autosize
              minRows={3}
              value={reminderBody}
              onChange={(e) => setReminderBody(e.currentTarget.value)}
            />
            <Group justify="flex-end">
              <Button
                size="xs"
                variant="light"
                color="brand"
                leftSection={<IconSparkles size={14} />}
                loading={suggestReminderTextM.isPending}
                disabled={!attEvent || sendReminderM.isPending}
                onClick={() => suggestReminderTextM.mutate()}
              >
                Text vorschlagen
              </Button>
            </Group>

            {undecidedRows.length > 0 && (
              <Card
                radius="md"
                p="sm"
                style={{
                  background: 'var(--app-surface-2)',
                  border: '1px solid var(--app-border-2)',
                }}
              >
                <Text c="dimmed" size="sm" mb={6}>
                  Vorschau Empfaenger (max 12)
                </Text>
                <Group gap={6}>
                  {undecidedRows.slice(0, 12).map((r) => (
                    <Badge key={r.user_id} className="contrast-badge-neutral" variant="light" color="gray">
                      {nameLabel(r)}
                    </Badge>
                  ))}
                  {undecidedRows.length > 12 && (
                    <Badge className="contrast-badge-neutral" variant="light" color="gray">
                      +{undecidedRows.length - 12}
                    </Badge>
                  )}
                </Group>
              </Card>
            )}

            <Group justify="flex-end">
              <Button variant="light" color="gray" className="contrast-secondary-btn" onClick={() => setReminderOpen(false)} disabled={sendReminderM.isPending}>
                Abbrechen
              </Button>
              <Button
                leftSection={<IconBellRinging size={16} />}
                color="brand"
                onClick={() => sendReminderM.mutate()}
                loading={sendReminderM.isPending}
                disabled={undecidedRows.length === 0}
              >
                Push senden
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <Modal
        opened={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setNotifyEventChange(false);
        }}
        title={
          modalMode === 'create'
            ? 'Neuer Termin'
            : modalMode === 'edit'
              ? 'Termin bearbeiten'
              : 'Termin duplizieren'
        }
        centered
        size="lg"
        overlayProps={{ blur: 2 }}
      >
        <Stack gap="sm">
          <TextInput
            label="Titel"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.currentTarget.value }))}
            withAsterisk
          />

          <Select
            label="Typ"
            placeholder="Auswaehlen"
            data={eventTypeOptions}
            value={form.event_type}
            onChange={(v) => setForm((f) => ({ ...f, event_type: v }))}
            searchable
            withAsterisk
          />

          <Group grow>
            <DateTimePicker
              label="Start"
              value={form.start_time}
              onChange={(d) =>
                setForm((f) => ({ ...f, start_time: dateTimeStringToDateLocal(d) }))
              }
              withAsterisk
              clearable={false}
            />
            <DateTimePicker
              label="Ende"
              value={form.end_time}
              onChange={(d) => setForm((f) => ({ ...f, end_time: dateTimeStringToDateLocal(d) }))}
              withAsterisk
              clearable={false}
            />
          </Group>

          <Select
            label="Ort"
            placeholder="Optional"
            data={predefinedLocations}
            value={form.location_name || null}
            onChange={(v) => setForm((f) => ({ ...f, location_name: v ?? '' }))}
            searchable
            clearable
            nothingFoundMessage="Kein Treffer"
          />

          <Switch
            label="Anwesenheits-Check"
            checked={form.requires_attendance_check}
            onChange={(e) =>
              setForm((f) => ({ ...f, requires_attendance_check: e.currentTarget.checked }))
            }
          />

          <Switch
            label={modalMode === 'edit' ? 'Push bei Aenderung senden' : 'Push bei Erstellung senden'}
            checked={notifyEventChange}
            onChange={(e) => setNotifyEventChange(e.currentTarget.checked)}
          />
          {notifyEventChange && (
            <Text c="dimmed" size="sm">
              {modalMode === 'edit'
                ? `Termin "${form.title.trim() || 'Titel'}" wurde abgeaendert.`
                : `Neuer Termin angelegt "${form.title.trim() || 'Titel'}" am ${
                    form.start_time ? form.start_time.toLocaleDateString('de-DE') : '-'
                  } von ${session?.user?.email ?? 'Admin'}.`}
            </Text>
          )}

          <Group grow>
            <MultiSelect
              label="Rollen"
              data={roleOptions}
              value={form.role_ids}
              onChange={(v) => setForm((f) => ({ ...f, role_ids: v }))}
              searchable
              clearable
              nothingFoundMessage="Keine Rolle"
            />
            <MultiSelect
              label="Ressorts"
              data={ressortOptions}
              value={form.ressort_ids}
              onChange={(v) => setForm((f) => ({ ...f, ressort_ids: v }))}
              searchable
              clearable
              nothingFoundMessage="Kein Ressort"
            />
          </Group>

          <Textarea
            label="Beschreibung"
            minRows={3}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.currentTarget.value }))}
          />

          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setModalOpen(false);
                setNotifyEventChange(false);
              }}
              disabled={busy}
            >
              Abbrechen
            </Button>
            <Button
              onClick={() => {
                if (!form.title.trim() || !form.event_type || !form.start_time || !form.end_time) {
                  notifications.show({ color: 'yellow', title: 'Pflichtfelder', message: 'Bitte Titel/Typ/Zeiten setzen.' });
                  return;
                }
                if (form.end_time.getTime() < form.start_time.getTime()) {
                  notifications.show({ color: 'yellow', title: 'Zeit', message: 'Ende darf nicht vor Start liegen.' });
                  return;
                }

                if (modalMode === 'edit' && editingId) {
                  updateM.mutate({ id: editingId, payload: form });
                } else {
                  createM.mutate(form);
                }
              }}
              loading={busy}
            >
              Speichern
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
